import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import ActionFollowButton from './ActionFollowButton'
import ActionStatusTag from './ActionStatusTag'
import ImportanceBadge from './ImportanceBadge'
import { formatDate } from '../../../lib/datetime'
import { deleteLabel } from '../../../lib/deleteLabel'
import { mainResponsibleOf, secondaryResponsiblesOf, type MeetingAction, type MinutePerson } from '../types'

/**
 * ActionsView'in dar ekran karsiligi. Ayni desen (ayri dosya): status + onem rozeti karsiligi ust
 * satirda, baslik altta. Salt-okuma oldugu icin islem alani yok; kart her zaman tiklanabilir (satirlarin
 * hepsi kayitli, id her zaman dolu).
 */
export default function ActionViewCards({
  actions,
  showRecordNumber,
  canFollow = false,
  onDelete,
}: Readonly<{
  actions: MeetingAction[]
  showRecordNumber: boolean
  /** 120 — kartta takip yıldızı çizilsin mi (kullanıcı koşulu: `useCanFollowActions`). */
  canFollow?: boolean
  /**
   * **Görevi sil** (2026-09-11) — masaüstü tablosundaki çöp kutusunun kart karşılığı; verilmezse
   * çizilmez. Eklenmeseydi dar ekranda (≤`CARD_VIEW_BREAKPOINT`) silme özelliği tümüyle kaybolurdu.
   */
  onDelete?: (action: MeetingAction) => void
}>) {
  const { t } = useTranslation()
  // Detaya giden link `useNavigateWithOrigin` ile aynı "geri dönülecek konum"u taşır (originNav →
  // OriginState.from); sarmalayıcı bir navigate yerine gerçek <Link> kullanıldığı için state elle verilir.
  const location = useLocation()
  const from = location.pathname + location.search
  return (
    <div className="ui-card-list act-editor-cards">
      {actions.map((row) => {
        const removeLabel = deleteLabel(t, 'actions.detail.delete.action', row.name)
        const mainName = mainResponsibleOf(row.assignees)?.fullName ?? ''
        const secondaryNames = secondaryResponsiblesOf(row.assignees)
          .map((p: MinutePerson) => p.fullName)
          .join(', ')
        return (
          <div key={row.id} className="ui-card ui-card--linked">
            <div className="ui-card-head">
              <ActionStatusTag statusId={row.statusId} name={row.statusName ?? ''} />
              <ImportanceBadge levelId={row.importanceLevelId} name={row.importanceLevelName} />
              {/* Yıldız + Sil kartın İÇİNDE ama artık interaktif bir atası YOK: kart kökü düz bir
                  <div>, tıklanabilirliği başlıktaki <Link>'in kaplama katmanı veriyor (ui.css →
                  `.ui-card--linked`). Düğmeler o katmanın üstünde kalır (`z-index`), yani gövde
                  tıklaması detaya gider, düğme tıklaması kendi eylemine. */}
              {(onDelete || canFollow) && (
                <div className="ui-card-actions">
                  {canFollow && (
                    <ActionFollowButton actionId={row.id} name={row.name} followed={row.followed ?? false} />
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      className="minutes-remove-btn"
                      aria-label={removeLabel}
                      title={removeLabel}
                      onClick={() => onDelete(row)}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <Link className="ui-card-link" to={`/minutes/actions/${row.id}`} state={{ from }}>
              <span className="ui-card-title">{row.name}</span>
            </Link>
            {showRecordNumber && row.recordNumber && (
              <span className="ui-card-sub">{row.recordNumber}</span>
            )}
            {row.description && <div className="ui-card-row act-card-desc">{row.description}</div>}
            {(mainName || secondaryNames) && (
              <div className="ui-card-row">
                {mainName && <span>{mainName}</span>}
                {secondaryNames && <span className="act-card-secondary">+ {secondaryNames}</span>}
              </div>
            )}
            {row.deadline && <div className="ui-card-row">{formatDate(row.deadline)}</div>}
          </div>
        )
      })}
    </div>
  )
}
